'use client';

import { mapErrorResponseIntoForm } from '@lib/form/form-validation';
import * as RadixForm from '@radix-ui/react-form';
import { revalidateLogic, useForm } from '@tanstack/react-form';
import { Alert } from '@tuic/web-compositions/core/Alert';
import { Button } from '@tuic/web-compositions/core/Button';
import { Divider } from '@tuic/web-compositions/core/Divider';
import { Skeleton } from '@tuic/web-compositions/core/Skeleton';
import { ManifestPinDialog } from '@tuic/web-compositions/my-trips/ManifestPinDialog';
import { AlertDialog } from '@tuic/web-compositions/shared/AlertDialog';
import { Fragment, useEffect, useMemo, useState } from 'react';

import { ContactSection } from './sections/ContactSection';
import { IdCardSection } from './sections/IdCardSection';
import { OtherInformationSection } from './sections/OtherInformationSection';
import { PersonalInfoSection } from './sections/PersonalInfoSection';
import { createManifestSchema } from './validation';
// VisumSection: scaffolding for visa-requiring destinations; rendered
// conditionally once the backend flag is wired up (Phase 7).
// import { VisumSection } from './sections/VisumSection';

import type {
  Manifest,
  ManifestFieldErrors,
  ManifestFormValues,
} from './types';
import type { SimpleFormApi } from '@lib/form/helper';
import type {
  I18NFromComponent,
  I18NInterface,
} from '@tuic/web-compositions/utils/i18n';
import type { FC, FormEvent, ReactNode } from 'react';
import type { ZodIssue } from 'zod';

export type ManifestFormProps = {
  fallback?: boolean;

  headline?: ReactNode;
  subline?: ReactNode;
  pinDialogHeadline?: ReactNode;
  pinDialogSubline?: ReactNode;

  manifest?: Manifest;

  /** First trip's startDate (ISO YYYY-MM-DD). Enables `dateOfIssue < embark`. */
  embarkDate?: string;
  /** Last trip's endDate (ISO YYYY-MM-DD). Enables `validUntil ≥ debark + N months`. */
  debarkDate?: string;

  entryRequirementsHref?: string;

  /** Called when the full form is submitted */
  onSubmit?: (values: ManifestFormValues) => void;

  /** Whether a submission is currently in progress */
  isSubmitting?: boolean;

  /** Field-level errors from the API (keyed by section then field) */
  fieldErrors?: ManifestFieldErrors | null;

  /** Generic server error (409/502/5xx) — shows banner */
  hasGenericError?: boolean;

  i18n?: I18NFromComponent<typeof PersonalInfoSection> &
    I18NFromComponent<typeof ContactSection> &
    I18NFromComponent<typeof IdCardSection> &
    I18NFromComponent<typeof OtherInformationSection> &
    I18NFromComponent<typeof ManifestPinDialog> &
    I18NInterface<
      | 'manifestGenericError'
      | 'manifestPreliminaryWarning'
      | 'formSave'
      | 'formRequiredNote'
      | 'manifestValidationRequired'
      | 'manifestValidationEmailInvalid'
      | 'manifestValidationDateFuture'
      | 'manifestValidationDatePast'
      | 'manifestValidationValidUntilAfterIssue'
      | 'manifestValidationMustAccept'
      | 'manifestValidationPhoneOrMobile'
      | 'manifestValidationEmergencyBothOrNeither'
      | 'manifestValidationValidUntilAfterEmbark'
      | 'manifestValidationZipCodeFormat'
      | 'manifestValidationPhoneFormat'
      | 'manifestValidationPassportNumberFormat'
      | 'contactAddressHintText'
      | 'contactAddressHintConfirm'
      | 'contactAddressHintCancel'
    >;

  isPinRequired?: boolean;
  onPinEnter?: (pin: string) => boolean;
};

const buildInitialValues = (manifest?: Manifest): ManifestFormValues => {
  const data = manifest?.manifestData;
  const personal = data?.personalInfo;
  const contact = data?.contact;
  const credentials = data?.credentials;
  const other = data?.otherInformation;
  const dietary = other?.dietary;
  const emergency = other?.emergencyContact;

  return {
    personalInfo: {
      salutation: personal?.salutation ?? '',
      title: personal?.title ?? '',
      firstName: personal?.firstName ?? '',
      lastName: personal?.lastName ?? '',
    },
    contact: {
      streetAddress: contact?.streetAddress ?? '',
      zipCode: contact?.zipCode ?? '',
      city: contact?.city ?? '',
      country: contact?.country ?? '',
      email: contact?.email ?? '',
      phone: contact?.phone ?? '',
      mobile: contact?.mobile ?? '',
      approveMobilePhoneDisclosure:
        contact?.approveMobilePhoneDisclosure ?? false,
    },
    credentials: {
      passportType: credentials?.passportType ?? '',
      nationality: credentials?.nationality ?? '',
      passportNumber: credentials?.passportNumber ?? '',
      placeOfBirth: credentials?.placeOfBirth ?? '',
      placeOfIssue: credentials?.placeOfIssue ?? '',
      dateOfBirth: credentials?.dateOfBirth ?? '',
      dateOfIssue: credentials?.dateOfIssue ?? '',
      birthName: credentials?.birthName ?? '',
      validUntil: credentials?.validUntil ?? '',
      profession: credentials?.profession ?? '',
      entryRequirementsAccepted: credentials?.entryRequirementsAccepted ?? false,
      hasVisa: credentials?.hasVisa ?? false,
    },
    otherInformation: {
      mealRequest: dietary?.mealRequest ?? '',
      individualRequest: dietary?.individualRequest ?? '',
      allergy: dietary?.allergy ?? [],
      allergyInfo: dietary?.allergyInfo ?? '',
      handicap: other?.handicap ?? '',
      emergencyContactName: emergency?.name ?? '',
      emergencyContactPhone: emergency?.phone ?? '',
      acceptDataProtectionTerms: other?.acceptDataProtectionTerms ?? false,
      acceptHealthTerms: other?.acceptHealthTerms ?? false,
      pregnant24Weeks: other?.pregnant24Weeks ?? false,
      preferredLanguageCode: data?.preferredLanguageCode ?? '',
    },
  };
};

// A street address without any digit is almost certainly missing a house
// number — we don't hard-reject it (some rural / international addresses
// really have no number), but we ask the user to confirm before submitting.
const addressMissingHouseNumber = (street: string): boolean =>
  !!street && !/\d/.test(street);

// Flatten `{ section: { field: msgs } }` to ZodIssue[] with dotted paths
// matching our TanStack field names (`personalInfo.salutation`, …).
const flattenFieldErrors = (
  fieldErrors: ManifestFieldErrors | null | undefined,
): ZodIssue[] => {
  if (!fieldErrors) return [];
  const issues: ZodIssue[] = [];
  for (const [section, sectionErrors] of Object.entries(fieldErrors)) {
    if (!sectionErrors || typeof sectionErrors !== 'object') continue;
    if (Array.isArray(sectionErrors)) {
      for (const message of sectionErrors) {
        issues.push({ code: 'custom', path: [section], message });
      }
      continue;
    }
    for (const [field, messages] of Object.entries(sectionErrors)) {
      if (!Array.isArray(messages)) continue;
      for (const message of messages) {
        issues.push({
          code: 'custom',
          path: [`${section}.${field}`],
          message,
        });
      }
    }
  }
  return issues;
};

export const ManifestForm: FC<ManifestFormProps> = ({
  fallback,
  headline,
  subline,
  pinDialogHeadline,
  pinDialogSubline,
  onPinEnter,
  onSubmit,
  isSubmitting,
  fieldErrors,
  hasGenericError,
  i18n,
  isPinRequired,
  manifest,
  embarkDate,
  debarkDate,
  entryRequirementsHref,
}) => {
  const schema = useMemo(
    () => createManifestSchema(i18n, { embarkDate, debarkDate }),
    [i18n, embarkDate, debarkDate],
  );

  // Address-hint dialog: when the schema passes but the street has no digits,
  // we park the validated values here and open the dialog. "Bestätigen" fires
  // the parent onSubmit with the parked values; "Bearbeiten" just closes.
  const [pendingSubmit, setPendingSubmit] = useState<ManifestFormValues | null>(
    null,
  );

  const form = useForm({
    defaultValues: buildInitialValues(manifest),
    // Classic "onSubmit first, then revalidate onChange" UX via TanStack's
    // built-in `revalidateLogic` — matches React Hook Form's default mode.
    validationLogic: revalidateLogic({
      mode: 'submit',
      modeAfterSubmission: 'change',
    }),
    validators: { onDynamic: schema },
    onSubmit: ({ value }) => {
      if (addressMissingHouseNumber(value.contact.streetAddress)) {
        setPendingSubmit(value);
        return;
      }
      onSubmit?.(value);
    },
  });

  // Merge server-side field errors into TanStack form meta when they change.
  useEffect(() => {
    const issues = flattenFieldErrors(fieldErrors);
    if (issues.length === 0) return;
    mapErrorResponseIntoForm(
      { success: false, message: 'validation failed', fieldErrors: issues },
      form,
    );
  }, [fieldErrors, form]);

  const fullName =
    `${manifest?.manifestData?.personalInfo?.firstName ?? ''} ${manifest?.manifestData?.personalInfo?.lastName ?? ''}`.trim();

  const isPreliminary = manifest?.preliminary === true;
  const isDisabled = isPreliminary || isSubmitting;

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    const formEl = event.currentTarget;
    void form.handleSubmit().then(() => {
      const fieldMeta = form.state.fieldMeta as Record<
        string,
        { errors?: unknown[] } | undefined
      >;
      const erroredFields = Object.entries(fieldMeta ?? {})
        .filter(([, meta]) => (meta?.errors?.length ?? 0) > 0)
        .map(([name]) => name);
      if (erroredFields.length === 0) return;

      // Scroll to and focus the first field (in DOM order) that has a
      // validation error. Some primitives (Select, PhoneNumberInput, Birthdate)
      // render a hidden `<input name=X>` alongside the visible controls —
      // we skip hidden matches and fall back to the nearest visible ancestor.
      const errored = new Set(erroredFields);
      const isVisible = (el: HTMLElement): boolean =>
        el.offsetParent !== null &&
        !(el instanceof HTMLInputElement && el.type === 'hidden');

      const candidates = Array.from(
        formEl.querySelectorAll<HTMLElement>('[name]'),
      ).filter(el => errored.has(el.getAttribute('name') ?? ''));

      const visible = candidates.find(isVisible);
      if (visible) {
        visible.scrollIntoView({ behavior: 'smooth', block: 'center' });
        visible.focus();
        return;
      }

      // All matches were hidden — walk up from the first match to a visible
      // ancestor and focus the first visible input inside it.
      const hidden = candidates[0];
      if (!hidden) return;
      let cursor: HTMLElement | null = hidden;
      while (cursor && !isVisible(cursor) && cursor !== formEl) {
        cursor = cursor.parentElement;
      }
      if (cursor && cursor !== formEl) {
        cursor.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const focusable = cursor.querySelector<HTMLElement>(
          'input:not([type=hidden]), textarea, button',
        );
        focusable?.focus();
      }
    });
  };

  // Cast to the shared type used by section props — bypasses TanStack's
  // 12-generic `ReactFormExtendedApi` at the component boundary.
  const sectionForm = form as unknown as SimpleFormApi<ManifestFormValues>;

  return (
    <div className="space-y-10 text-marine-high-emphasis">
      <div className="space-y-4">
        {fallback ? (
          <Skeleton className="h-8 w-1/2" />
        ) : (
          headline && <h1 className="headline-lg">{headline}</h1>
        )}
        {fallback ? (
          <div className="space-y-3">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        ) : (
          subline && <div className="text-lg">{subline}</div>
        )}
      </div>

      {hasGenericError && (
        <Alert mode="error">{i18n?.manifestGenericError}</Alert>
      )}

      {isPreliminary && (
        <Alert mode="information-white">
          {i18n?.manifestPreliminaryWarning}
        </Alert>
      )}

      {fallback && (
        <div className="space-y-4">
          {new Array(5).fill(null).map((_, index) => (
            <Fragment key={index}>
              <Skeleton key={index} className="h-10" />
              <Divider align="horizontal" />
            </Fragment>
          ))}
        </div>
      )}

      {!fallback && (
        <RadixForm.Root
          onSubmit={handleFormSubmit}
          className="space-y-8"
          noValidate
        >
          <PersonalInfoSection
            form={sectionForm}
            manifest={manifest}
            disabled={isDisabled}
            i18n={i18n}
          />
          <Divider align="horizontal" />
          <ContactSection
            form={sectionForm}
            manifest={manifest}
            disabled={isDisabled}
            i18n={i18n}
          />
          <Divider align="horizontal" />
          <IdCardSection
            form={sectionForm}
            manifest={manifest}
            isDisabled={isDisabled}
            entryRequirementsHref={entryRequirementsHref}
            i18n={i18n}
          />
          <Divider align="horizontal" />

          {/* <VisumSection i18n={i18n} /> — enable when entryVisa flag is live */}

          <OtherInformationSection
            form={sectionForm}
            manifest={manifest}
            isDisabled={isDisabled}
            i18n={i18n}
          />

          <div className="flex items-center justify-between">
            <p className="text-sm text-marine-medium-emphasis">
              {i18n?.formRequiredNote}
            </p>
            <RadixForm.Submit asChild>
              <Button
                type="submit"
                indicating={isSubmitting}
                disabled={isPreliminary}
              >
                {i18n?.formSave}
              </Button>
            </RadixForm.Submit>
          </div>
        </RadixForm.Root>
      )}

      {isPinRequired && (
        <ManifestPinDialog
          open
          closeable={false}
          headline={pinDialogHeadline}
          subline={pinDialogSubline}
          onPinEnter={onPinEnter}
          travellerName={fullName}
          i18n={i18n}
        />
      )}

      <AlertDialog
        open={pendingSubmit !== null}
        onOpenChange={open => {
          if (!open) setPendingSubmit(null);
        }}
        actions={[
          { title: i18n?.contactAddressHintCancel ?? 'Bearbeiten' },
          {
            title: i18n?.contactAddressHintConfirm ?? 'Bestätigen',
            primary: true,
            onClick: () => {
              const toSubmit = pendingSubmit;
              setPendingSubmit(null);
              if (toSubmit) onSubmit?.(toSubmit);
            },
          },
        ]}
      >
        {i18n?.contactAddressHintText}
      </AlertDialog>
    </div>
  );
};
